import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportCrewDetails from "hooks/organization/reports/types/useGetReportCrewDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableCrewDetails(startDate, endDate) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportCrewDetails, { data, loading, error }] = useGetReportCrewDetails(false);

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        page,
        first,
      };
      getReportCrewDetails({ variables });
    },
    [startDate, endDate, getReportCrewDetails]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportCrewDetails?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Crew Name",
        accessor: "crew_name",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Job Id",
        accessor: "job_id",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Job size",
        accessor: "job_size",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Material cost type",
        accessor: "material_cost_type",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Proposal Total Amount",
        accessor: "proposal_total_amount",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Estimated Labor Hours",
        accessor: "estimated_labour_hours",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Actual Labor Hours",
        accessor: "actual_labour_hours",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Total Material Used",
        accessor: "total_material_used",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportCrewDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportCrewDetails: paginate,
  } as const;
}
